import React, { useEffect, useState } from "react";
import styled from "styled-components";
import colors from "styles/colors";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FacebookCircle } from "@styled-icons/boxicons-logos/FacebookCircle";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";

const FacebookSigninButton = ({ reload = null, currentTab }) => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const leadFormId = localStorage.getItem("leadFormId");
  const fbDisconnectedResult = useSelector((state) => facebookSelectors.setFbDisconnectedResult(state));
  const facebookError = useSelector((state) => facebookSelectors.setFacebookErrorResult(state));
  const [showLoadingContainer, setShowLoadingContainer] = useState(false);

  /**
   * if have error, back to login button
   */
  const backToLogin = async () => {
    FB.logout();
    await dispatch(
      facebookActions.getFacebookUserInfo({
        token: rmpToken,
        userAccessToken: null,
      }),
    );
    setShowLoadingContainer(false);
    await dispatch(facebookActions.setFacebookError(true));
  };

  /**
   * after click sign in button, get all facebook data (left side of setting modal)
   */
  const getFacebookApiToken = async (response) => {
    const facebookSetting = await dispatch(
      facebookActions.getFacebookPageSetting({
        token: rmpToken,
        id: leadFormId,
      }),
    );

    const result = await dispatch(
      facebookActions.getFacebookApiToken({
        token: rmpToken,
        access_token: response.authResponse.accessToken,
      }),
    );

    if (result.result === 0) {
      await dispatch(facebookActions.setFacebookFormLoading(true));
      const userInfo = await dispatch(
        facebookActions.getFacebookUserInfo({
          token: rmpToken,
          userAccessToken: response.authResponse.accessToken,
        }),
      );

      if (userInfo.result === 0 && userInfo.msg === "GET_DATA_COMPLETED") {
        await dispatch(
          facebookActions.getFacebookPageByUserId({
            token: rmpToken,
            userId: userInfo.data.id,
          }),
        );

        const fbPageList = await dispatch(
          facebookActions.getFacebookPageList({
            token: rmpToken,
            userAccessToken: response.authResponse.accessToken,
            loadMore: null,
          }),
        );

        if (fbPageList.result === 0 && fbPageList.data.pageList !== null && fbPageList.data.pageList.length > 0) {
          let fbPageUrl = fbPageList.data.pageList[0].link;
          if (
            !fbDisconnectedResult.data.fbDisconnected &&
            facebookSetting.result === 0 &&
            facebookSetting.msg === "GET_DATA_COMPLETED" &&
            facebookSetting.data &&
            facebookSetting.data.facebook_message_error === "token_expire"
          ) {
            fbPageUrl = facebookSetting.data.fb_page_url;
          }
          const pageIdResult = await dispatch(
            facebookActions.getFacebookPageId({
              token: rmpToken,
              userAccessToken: response.authResponse.accessToken,
              pageUrl: fbPageUrl,
            }),
          );

          if (pageIdResult.result === 0 && pageIdResult.msg === "GET_DATA_COMPLETED") {
            await dispatch(facebookActions.setFacebookLoading(false));
            const formList = await dispatch(
              facebookActions.getFacebookFormList({
                token: rmpToken,
                pageAccessToken: pageIdResult.data.access_token,
                pageUrl: fbPageUrl,
                appReserve: currentTab,
              }),
            );

            if (formList.result === 0) {
              await dispatch(facebookActions.setValidateFacebookLeadForm({ result: false }));
              if (formList.data.formList !== null) {
                await dispatch(facebookActions.setFbConnected(true));
                let radioValue = "all";
                let pageUrlInput = fbPageList.data.pageList[0].link;
                let pageFormSelectType = "all";
                let checkedFormItems = [];

                if (formList.data.formStatus == "SOMEUSED" || formList.data.formStatus == "ALLUSED") {
                  radioValue = "select";
                  pageFormSelectType = "select";
                }

                if (
                  !fbDisconnectedResult.data.fbDisconnected &&
                  facebookSetting.result === 0 &&
                  facebookSetting.msg === "GET_DATA_COMPLETED" &&
                  facebookSetting.data &&
                  facebookSetting.data.facebook_message_error === "token_expire"
                ) {
                  radioValue = facebookSetting.data.facebook_form_setting;
                  pageUrlInput = facebookSetting.data.fb_page_url;
                  pageFormSelectType = facebookSetting.data.facebook_form_setting;
                  checkedFormItems = facebookSetting.data.leadForm
                    .filter((item) => item.status === "active")
                    .map((item) => item.fb_form_id);
                }

                localStorage.setItem("radioValue", radioValue);
                await dispatch(facebookActions.changePageUrlInput(pageUrlInput));
                await dispatch(facebookActions.changePageFormSelectType(pageFormSelectType));
                await dispatch(facebookActions.setCheckedFormItems(checkedFormItems));
                if (showLoadingContainer) {
                  setShowLoadingContainer(false);
                }
              } else {
                await dispatch(facebookActions.setFbConnected(true));
                let radioValue = null;
                let pageUrlInput = fbPageList.data.pageList[0].link;
                let pageFormSelectType = null;
                let checkedFormItems = null;

                localStorage.setItem("radioValue", radioValue);
                await dispatch(facebookActions.changePageUrlInput(pageUrlInput));
                await dispatch(facebookActions.changePageFormSelectType(pageFormSelectType));
                await dispatch(facebookActions.setCheckedFormItems(checkedFormItems));
                if (showLoadingContainer) {
                  setShowLoadingContainer(false);
                }
              }
            } else {
              backToLogin();
            }
          } else {
            await dispatch(facebookActions.setFacebookLoading(false));
            backToLogin();
          }
        } else {
          await dispatch(facebookActions.setFacebookLoading(false));
          backToLogin();
        }
        await dispatch(facebookActions.setFacebookFormLoading(false));
      } else {
        await dispatch(facebookActions.setFacebookLoading(false));
        backToLogin();
        await dispatch(facebookActions.setFacebookFormLoading(false));
      }
    } else {
      await dispatch(facebookActions.setFacebookLoading(false));
      facebookReLogin();
    }
  };

  /**
   * login facebook
   */
  const facebookLogin = () => {
    dispatch(facebookActions.setFacebookLoading(true));
    dispatch(facebookActions.setFacebookError(false));
    setShowLoadingContainer(true);
    FB.getLoginStatus(function (response) {
      if (response.status == "connected") {
        getFacebookApiToken(response);
      } else {
        FB.login(
          function (response) {
            if (response.authResponse) {
              getFacebookApiToken(response);
            } else {
              setShowLoadingContainer(false);
            }
          },
          {
            scope: "ads_management,pages_show_list,pages_read_engagement,leads_retrieval,pages_manage_ads",
            return_scopes: true,
          },
        );
      }
    });
  };

  const facebookReLogin = () => {
    FB.getLoginStatus(function (response) {
      if (response.status === "connected") {
        FB.logout(function () {
          facebookLogin();
        });
      } else {
        if (showLoadingContainer) {
          setShowLoadingContainer(false);
        }
      }
    });
  };

  const ErrorMessage = () => {
    return (
      <MessageError className="error-message">
        <div>{t("__connection_problem")}</div>
        <div>{t("__connection_try_again")}</div>
      </MessageError>
    );
  };

  return (
    <>
      <SiginContainer>
        {!showLoadingContainer ? (
          <>
            {facebookError.data.error ? (
              <ErrorMessage />
            ) : (
              <div className="login-description">{t("__fbleads_fb_connect")}</div>
            )}
            <StyledFacebookButton onClick={facebookLogin}>
              <i className="fab fa-facebook-square"></i>
              <span id="fb_button_text">
                <StyledFacebookIcon />
                &nbsp;{t("__fbads_setup_facebook_login_button")}
              </span>
            </StyledFacebookButton>
          </>
        ) : (
          <LoadingArea>
            <StyledLoadingIcon></StyledLoadingIcon>
          </LoadingArea>
        )}
      </SiginContainer>
    </>
  );
};

export default FacebookSigninButton;

const SiginContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  .login-description {
    font-size: 14px;
    color: #aaa;
    margin-bottom: 5px;
    text-align: center;
  }
  .warning-description {
    font-size: 12px;
    color: ${colors.negative};
    margin-bottom: 5px;
    text-align: center;
  }
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 490px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StyledFacebookButton = styled.button`
  color: #fff;
  background-color: #1578f1;
  border: none;
  border-radius: 4px;
  padding: 5px 20px;
  cursor: pointer;
`;

const StyledFacebookIcon = styled(FacebookCircle)`
  width: 25px;
`;

const MessageError = styled.div`
  color: ${colors.negative};
  font-size: 14px;
  text-align: center;
  line-height: 1.5rem;
  margin-bottom: 5px;
`;
